import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, Share2, Image as ImageIcon, Send, Eye, TrendingUp, Play, Video, Radio, X, Volume2, VolumeX, Repeat2, Sparkles, ThumbsDown, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import FullscreenImageViewer from "@/components/FullscreenImageViewer";
import VideoReels from "@/components/VideoReels";
import DailyMissions from "@/components/DailyMissions";
import StoriesRow from "@/components/StoriesRow";
import PostMenu, { getHiddenPostIds, hidePostLocally, unhidePostLocally } from "@/components/PostMenu";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import LiveBroadcastDialog from "@/components/LiveBroadcastDialog";
import ScheduledLivesRow from "@/components/ScheduledLivesRow";
import LiveViewerDialog from "@/components/LiveViewerDialog";
import PostReactions from "@/components/PostReactions";
import BoostPostDialog from "@/components/BoostPostDialog";
import AllUsersStrip from "@/components/AllUsersStrip";
import ExpandableText from "@/components/ExpandableText";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import useLinkPreview from "@/hooks/useLinkPreview";
import MentionPicker from "@/components/MentionPicker";
import PostCarousel from "@/components/PostCarousel";
import PageSuggestions from "@/components/PageSuggestions";
import CompleteLocationBanner from "@/components/CompleteLocationBanner";
import TagPeoplePicker from "@/components/TagPeoplePicker";
import { TaggedUsers } from "@/pages/PostDetail";
import AuthorBadges from "@/components/AuthorBadges";
import CommentThread from "@/components/CommentThread";
import { MusicPicker, LocationPicker, MusicTag, LocationTag } from "@/components/MusicLocation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PostCard({ post: p, user, t, toggleLike, openC, setOpenComments, openComments, updateComments, autoplayEnabled, videoMuted, setVideoMuted, onOpenFullscreen, onEdited, onDeleted, onHidden, onReposted, onNotInterested, onBoost }) {
  const liked = p.likes?.includes(user?.user_id);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [viewed, setViewed] = useState(false);
  const [sharesLocal, setSharesLocal] = useState(p.share_count || 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const isRepost = !!p.is_repost;

  // Dwell-time tracking: measure how long the post stays ≥60% visible and report once on leave/unmount
  useEffect(() => {
    if (!cardRef.current || p.user_id === user?.user_id) return;
    let since = null;
    let sent = false;
    const report = () => {
      if (sent || since === null) return;
      const secs = (Date.now() - since) / 1000;
      since = null;
      if (secs < 3) return;
      sent = true;
      axios.post(`${API}/content/dwell`, { post_id: p.post_id, seconds: Math.round(secs) }).catch(() => {});
    };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) { if (since === null) since = Date.now(); }
        else report();
      });
    }, { threshold: [0, 0.6] });
    obs.observe(cardRef.current);
    return () => { report(); obs.disconnect(); };
  }, [p.post_id, p.user_id, user?.user_id]);

  // View tracking observer — fires ONLY after the post has been visible for ~900ms
  // (dwell time). This prevents a burst of 100+ concurrent /content/event requests
  // when the user scrolls fast through the feed (which was causing ingress 502s).
  useEffect(() => {
    if (viewed || !cardRef.current) return;
    let timer = null;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !viewed) {
          if (timer) return;
          timer = setTimeout(() => {
            setViewed(true);
            axios.post(`${API}/content/event`, { post_id: p.post_id, event: "view" })
              .then(() => {})
              .catch(() => {});
          }, 900);
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      });
    }, { threshold: 0.6 });
    obs.observe(cardRef.current);
    return () => { if (timer) clearTimeout(timer); obs.disconnect(); };
  }, [p.post_id, viewed]);

  // Video autoplay controlled by intersection + toggle
  useEffect(() => {
    if (!p.video_url || !videoRef.current) return;
    const vid = videoRef.current;
    if (!autoplayEnabled) {
      vid.pause();
      setIsPlaying(false);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          vid.play().then(() => setIsPlaying(true)).catch(() => {
            // Browser blocked autoplay with sound → force mute and retry once
            if (!vid.muted) {
              vid.muted = true;
              setVideoMuted(true);
              vid.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            } else {
              setIsPlaying(false);
            }
          });
        } else {
          vid.pause();
          setIsPlaying(false);
        }
      });
    }, { threshold: [0, 0.6, 1] });
    obs.observe(vid);
    return () => obs.disconnect();
  }, [p.video_url, autoplayEnabled, setVideoMuted]);

  const doShare = async () => {
    const shareUrl = `${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/feed#post-${p.post_id}`;
    const text = `${p.author_name}: ${(p.content || "").slice(0, 80)}...`;
    // 1) Public repost — visible to every user in the feed
    try {
      const rp = await axios.post(`${API}/posts/${p.post_id}/repost`, { comment: "" });
      if (rp.data?.already) {
        toast.info("Você já compartilhou este post no feed");
      } else {
        setSharesLocal(s => s + 1);
        toast.success("Compartilhado no feed para todos!");
        onReposted?.(rp.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível compartilhar");
      return;
    }
    // 2) Native share / copy link (best-effort)
    let shared = false;
    if (navigator.share) {
      try {
        await navigator.share({ title: "FUTURO PDV", text, url: shareUrl });
        shared = true;
      } catch (err) {
        console.debug("[Feed] navigator.share aborted", err?.message);
      }
    }
    if (!shared) {
      try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiado!"); } catch { /* clipboard blocked */ }
    }
    // 3) Coins for sharing (once per post)
    try {
      const r = await axios.post(`${API}/content/event`, { post_id: p.post_id, event: "share" });
      if (r.data.awarded > 0) toast.success(`+${r.data.awarded} FUTURO PDV por compartilhar!`);
    } catch (err) {
      console.error("[Feed] share event failed", err);
    }
  };

  const gallery = Array.isArray(p.image_urls) && p.image_urls.length ? p.image_urls : (p.image_url ? [p.image_url] : []);

  const mediaBlock = (
    <>
        {(() => {
          if (gallery.length === 0) return null;
          return (
            <div>
              {p.album_title && (
                <div
                  data-testid={`post-album-header-${p.post_id}`}
                  className="flex items-center gap-2 mb-2 px-1"
                >
                  <span className="text-base">📚</span>
                  <div>
                    <div className="text-sm font-semibold leading-tight">{p.album_title}</div>
                    <div className="text-[11px] text-muted-foreground">Álbum · {gallery.length} fotos</div>
                  </div>
                </div>
              )}
              <PostCarousel
                urls={gallery}
                onOpenFullscreen={(payload) => onOpenFullscreen?.({ ...payload, music: p.music })}
                testidPrefix={`post-carousel-${p.post_id}`}
              />
            </div>
          );
        })()}
        {p.video_url && (
          <div className="relative mb-3 rounded-xl overflow-hidden bg-black border border-border" data-testid={`post-video-${p.post_id}`}>
            <video
              ref={videoRef}
              src={p.video_url}
              poster={p.cover_url || undefined}
              className="w-full max-h-[70vh] object-contain cursor-pointer"
              playsInline
              muted={videoMuted}
              loop
              controls={!autoplayEnabled}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={() => setVideoError(true)}
              onClick={(e) => {
                // Open TikTok/Reels-style vertical video feed starting at this post
                if (autoplayEnabled && !videoError) {
                  e.preventDefault();
                  onOpenFullscreen?.({ postId: p.post_id });
                }
              }}
            />
            {videoError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm p-4 text-center">
                Vídeo indisponível
              </div>
            )}
            {autoplayEnabled && !videoError && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenFullscreen?.({ postId: p.post_id });
                  }}
                  data-testid={`post-video-fullscreen-${p.post_id}`}
                  className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  aria-label="Assistir em tela cheia"
                >
                  <Play className="w-5 h-5 ml-0.5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !videoMuted;
                    setVideoMuted(next);
                    // If unmuting, ensure this video is playing so user hears it
                    const vid = videoRef.current;
                    if (vid && !next && vid.paused) {
                      vid.play().then(() => setIsPlaying(true)).catch(() => {});
                    }
                  }}
                  data-testid={`post-video-mute-${p.post_id}`}
                  className="absolute bottom-3 left-3 w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  aria-label={videoMuted ? "Ativar som" : "Silenciar"}
                >
                  {videoMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                {videoMuted && (
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold uppercase tracking-wider pointer-events-none">
                    Toque para ativar som
                  </div>
                )}
              </>
            )}
          </div>
        )}
    </>
  );

  return (
    <Card ref={cardRef} className="p-5 animate-pop-in" data-testid={`post-${p.post_id}`}>
      {isRepost && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 pb-2 border-b border-border/60" data-testid={`repost-header-${p.post_id}`}>
          <Repeat2 className="w-4 h-4" />
          <Link to={`/user/${p.user_id}`} className="font-semibold hover:underline">{p.author_name}</Link>
          <span>compartilhou</span>
          {p.original_user_name && (
            <>
              <span>de</span>
              <Link to={`/user/${p.original_user_id}`} className="font-semibold hover:underline" data-testid={`repost-original-author-${p.post_id}`}>{p.original_user_name}</Link>
            </>
          )}
        </div>
      )}
      {p.recommend_reason && (
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary/80 bg-primary/10 px-2 py-0.5 rounded-full" data-testid={`recommend-reason-${p.post_id}`}>
            <Sparkles className="w-3 h-3" /> {p.recommend_reason}
          </div>
          {p.user_id !== user?.user_id && onNotInterested && (
            <button
              type="button"
              onClick={() => onNotInterested(p)}
              data-testid={`not-interested-${p.post_id}`}
              className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-2 py-0.5 rounded-full hover:bg-muted transition-colors"
              title="Ver menos disso"
            >
              <ThumbsDown className="w-3 h-3" /> Ver menos disso
            </button>
          )}
        </div>
      )}
      <div className="flex items-start gap-3 mb-4">
        <Link to={`/user/${p.user_id}`} className="hover:opacity-80" data-testid={`post-avatar-${p.post_id}`}>
          <Avatar className="w-11 h-11">
            <AvatarImage src={p.author_picture} />
            <AvatarFallback>{p.author_name?.[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/user/${p.user_id}`} className="font-semibold hover:underline" data-testid={`post-author-${p.post_id}`}>{p.author_name}</Link>
            <AuthorBadges badges={p.author_badges} testid={`post-author-badges-${p.post_id}`} />
            <TaggedUsers users={p.tagged_users} testid={`post-tagged-${p.post_id}`} />
            {p.visibility === "private" && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300" title="Só seguidores podem ver" data-testid={`post-private-${p.post_id}`}>🔒 Privado</span>
            )}
            {p.is_live_recording && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-300 font-bold uppercase tracking-wide" title="Gravação de transmissão ao vivo" data-testid={`post-live-replay-${p.post_id}`}>Live gravada</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</div>
        </div>
        <PostMenu
          post={p}
          user={user}
          onEdited={onEdited}
          onDeleted={onDeleted}
          onHidden={onHidden}
          onNotInterested={onNotInterested}
        />
      </div>
      {p.content && <ExpandableText text={p.content} mentions={p.mentions} className="text-base mb-3" testid={`post-text-${p.post_id}`} />}
      {!isRepost && p.link_preview && <LinkPreviewCard preview={p.link_preview} testid={`post-link-${p.post_id}`} />}
      {!isRepost && (p.music || p.location) && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`post-tags-${p.post_id}`}>
          <MusicTag music={p.music} testid={`post-music-${p.post_id}`} />
          <LocationTag location={p.location} testid={`post-location-${p.post_id}`} />
        </div>
      )}
      {isRepost && (p.original_content || gallery.length > 0 || p.video_url || p.link_preview) && (
        <div className="mb-3 rounded-xl border border-dashed border-border bg-muted/40 p-3" data-testid={`repost-original-${p.post_id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Link to={`/user/${p.original_user_id}`} className="hover:opacity-80">
              <Avatar className="w-7 h-7">
                <AvatarImage src={p.original_user_picture} />
                <AvatarFallback>{p.original_user_name?.[0]}</AvatarFallback>
              </Avatar>
            </Link>
            <div className="leading-tight">
              <Link to={`/user/${p.original_user_id}`} className="text-sm font-semibold hover:underline">{p.original_user_name || "Usuário"}</Link>
              {p.original_created_at && <div className="text-[10px] text-muted-foreground">{new Date(p.original_created_at).toLocaleString()}</div>}
            </div>
          </div>
          {p.original_content && <ExpandableText text={p.original_content} className="text-sm opacity-90 mb-2" testid={`post-original-text-${p.post_id}`} />}
          {p.link_preview && <LinkPreviewCard preview={p.link_preview} testid={`post-original-link-${p.post_id}`} />}
          {(p.music || p.location) && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <MusicTag music={p.music} testid={`post-original-music-${p.post_id}`} />
              <LocationTag location={p.location} testid={`post-original-location-${p.post_id}`} />
            </div>
          )}
          <div data-testid={`repost-original-media-${p.post_id}`}>{mediaBlock}</div>
          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1" data-testid={`repost-credit-${p.post_id}`}>
            <Repeat2 className="w-3 h-3" /> Publicação original de <Link to={`/user/${p.original_user_id}`} className="font-semibold hover:underline">{p.original_user_name || "Usuário"}</Link>
            {p.original_post_id && <> · <Link to={`/post/${p.original_post_id}`} className="hover:underline">ver original</Link></>}
          </div>
        </div>
      )}
      {!isRepost && mediaBlock}
      <div className="flex items-center justify-between text-sm text-muted-foreground pt-3 border-t border-border">
        <PostReactions postId={p.post_id} initialReactions={p.reactions} initialMy={p.my_reaction} initialTotal={p.reactions_total ?? (p.likes?.length || 0)} />
        <button data-testid={`comment-btn-${p.post_id}`} onClick={() => setOpenComments({ ...openComments, [p.post_id]: !openC })} className="flex items-center gap-2 hover:text-primary transition-colors">
          <MessageCircle className="w-5 h-5" /> {p.comments?.length || 0}
        </button>
        <button data-testid={`share-${p.post_id}`} onClick={doShare} className="flex items-center gap-2 hover:text-primary transition-colors">
          <Share2 className="w-5 h-5" /> <span className="text-sm">{sharesLocal || 0}</span>
        </button>
        <button
          data-testid={`boost-${p.post_id}`}
          onClick={() => onBoost?.(p)}
          title="Impulsionar esta publicação como anúncio"
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm hover:brightness-110 active:scale-95 transition"
        >
          <Rocket className="w-4 h-4" /> Impulsionar
        </button>
      </div>
      <Collapsible open={!!openC}>
        <CollapsibleContent className="mt-4 space-y-3">
          <CommentThread
            postId={p.post_id}
            comments={p.comments || []}
            inputTestId={p.post_id}
            onNewComment={(c) => updateComments(p.post_id, (cs) => [...cs, c])}
            onLocalUpdate={(u) => updateComments(p.post_id, (cs) => cs.map((c) => c.comment_id === u.comment_id ? u : c))}
            onEdited={(u) => updateComments(p.post_id, (cs) => cs.map((c) => c.comment_id === u.comment_id ? u : c))}
            onDeleted={(id) => updateComments(p.post_id, (cs) => cs.filter((c) => c.comment_id !== id && c.parent_comment_id !== id))}
          />
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function FullscreenVideoPlayer({ video, onClose }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(video?.muted ?? true);

  useEffect(() => {
    if (!video) return;
    setMuted(video.muted ?? true);
    const el = videoRef.current;
    if (el) {
      el.currentTime = 0;
      el.play().catch(() => {});
    }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [video, onClose]);

  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black flex items-center justify-center animate-fade-in"
      data-testid="feed-video-fullscreen"
      onClick={onClose}
    >
      <div
        className="relative max-w-[95vw] max-h-[85vh] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <video
          ref={videoRef}
          src={video.url}
          poster={video.poster || undefined}
          className="w-full max-h-[85vh] object-contain rounded-lg"
          playsInline
          controls
          autoPlay
          muted={muted}
        />
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="feed-video-fullscreen-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
        data-testid="feed-video-fullscreen-mute"
        className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
        aria-label={muted ? "Ativar som" : "Silenciar"}
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </button>
    </div>
  );
}

function LivesRow({ lives, onOpenLive }) {
  if (!lives.length) return null;
  return (
    <Card className="p-4 overflow-hidden" data-testid="lives-row">
      <div className="flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4 text-red-500 animate-pulse" />
        <div className="text-sm font-bold uppercase tracking-wider text-red-500">Ao vivo agora</div>
        <div className="text-xs text-muted-foreground">· {lives.length} transmissão(ões)</div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {lives.map((l) => (
          <button
            key={l.live_id}
            data-testid={`live-thumb-${l.live_id}`}
            onClick={() => onOpenLive(l.live_id)}
            className="flex-shrink-0 w-40 rounded-xl border border-border hover:border-red-500 transition-colors overflow-hidden group text-left"
          >
            <div className="relative w-full h-24 bg-gradient-to-br from-red-500/20 via-red-500/10 to-transparent flex items-center justify-center">
              {l.thumbnail_url
                ? <img src={l.thumbnail_url} alt="" className="w-full h-full object-cover" />
                : <Radio className="w-8 h-8 text-red-500/60 group-hover:scale-110 transition-transform" />}
              <span className="absolute top-1 left-1 bg-red-500 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
              <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                <Eye className="w-3 h-3 inline mr-1" />{l.viewer_count || 0}
              </span>
              {l.is_recording && (
                <span data-testid={`live-thumb-rec-${l.live_id}`} title="Esta live está sendo gravada" className="absolute top-1 right-1 bg-black/60 text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> REC
                </span>
              )}
            </div>
            <div className="p-2">
              <div className="text-xs font-semibold truncate">{l.title}</div>
              <div className="text-[10px] text-muted-foreground truncate">{l.streamer_name}</div>
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  // "Criar" menu deep-link: /feed?compose=photo|video|text → focus composer (+ open picker)
  const { search: composeSearch } = useLocation();
  useEffect(() => {
    const kind = new URLSearchParams(composeSearch).get("compose");
    if (!kind) return;
    const t = setTimeout(() => {
      document.getElementById("feed-composer")?.scrollIntoView({ behavior: "smooth", block: "center" });
      document.querySelector('[data-testid="post-content"]')?.focus();
      const picker = kind === "photo" ? "post-upload-btn" : kind === "video" ? "post-upload-video-btn" : null;
      if (picker) document.querySelector(`[data-testid="${picker}"]`)?.click();
    }, 400);
    return () => clearTimeout(t);
  }, [composeSearch]);
  const [suggestions, setSuggestions] = useState([]);
  const [newerCount, setNewerCount] = useState(0);
  const [visibility, setVisibility] = useState("public"); // "public" | "private"
  const [content, setContent] = useState("");
  const linkPrev = useLinkPreview(content);
  const [mentionPicked, setMentionPicked] = useState([]);
  const contentRef = useRef(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imageUrls, setImageUrls] = useState([]);
  const [albumTitle, setAlbumTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [music, setMusic] = useState(null);
  const [location, setLocation] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [lives, setLives] = useState([]);
  const [scheduledLives, setScheduledLives] = useState([]);
  const [scheduledToStart, setScheduledToStart] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [viewerLiveId, setViewerLiveId] = useState(null);
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [reelsStartId, setReelsStartId] = useState(null);
  const [hiddenIds, setHiddenIds] = useState(() => getHiddenPostIds());
  // Feed always shows the newest posts first (mode selector removed by request)
  const feedMode = "recent";
  const missionsRef = useRef(null);


  const [autoplayEnabled] = useState(() => {
    const v = localStorage.getItem("feed_video_autoplay");
    return v === null ? true : v === "true";
  });
  const [videoMuted, setVideoMuted] = useState(() => {
    const v = localStorage.getItem("feed_video_muted");
    return v === null ? true : v === "true";
  });

  useEffect(() => {
    localStorage.setItem("feed_video_autoplay", String(autoplayEnabled));
  }, [autoplayEnabled]);

  useEffect(() => {
    localStorage.setItem("feed_video_muted", String(videoMuted));
  }, [videoMuted]);

  const loadPosts = async (mode = feedMode) => {
    if (mode === "for_you") {
      try {
        const res = await axios.get(`${API}/posts/recommended?limit=60`);
        setPosts(res.data);
        setNewerCount(0);
        return;
      } catch { /* fall back to chronological */ }
    }
    const res = await axios.get(`${API}/posts`);
    setPosts(res.data);
    setNewerCount(0);
  };
  const loadSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/posts/suggestions?limit=6`);
      setSuggestions(res.data || []);
    } catch { /* silent */ }
  };
  const checkNewer = async () => {
    if (feedMode !== "recent") return;
    try {
      const res = await axios.get(`${API}/posts`);
      const currentTop = posts[0]?.post_id;
      if (!currentTop) return;
      const idx = (res.data || []).findIndex(p => p.post_id === currentTop);
      const diff = idx >= 0 ? idx : (res.data || []).length;
      if (diff > 0) setNewerCount(diff);
    } catch {}
  };
  const loadLives = async () => {
    try {
      const res = await axios.get(`${API}/lives`);
      setLives(res.data || []);
    } catch (err) {
      console.error("[Feed] loadLives failed", err);
    }
    try {
      const rs = await axios.get(`${API}/lives/scheduled`);
      setScheduledLives(rs.data || []);
    } catch { /* silent */ }
  };
  const cancelScheduledLive = async (s) => {
    if (!window.confirm(`Cancelar a live agendada "${s.title}"?`)) return;
    try {
      await axios.delete(`${API}/lives/scheduled/${s.scheduled_id}`);
      setScheduledLives((prev) => prev.filter((x) => x.scheduled_id !== s.scheduled_id));
      toast.success("Agendamento cancelado");
    } catch { toast.error("Erro ao cancelar agendamento"); }
  };

  useEffect(() => {
    loadLives();
    loadSuggestions();
    const iv = setInterval(loadLives, 15000);
    const nv = setInterval(checkNewer, 20000);
    const sv = setInterval(loadSuggestions, 60000);
    return () => { clearInterval(iv); clearInterval(nv); clearInterval(sv); };
    // eslint-disable-next-line
  }, [posts.length && posts[0]?.post_id, feedMode]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPosts(feedMode); }, [feedMode]);

  // Claim weekly ranking rewards on Feed mount (idempotent per ISO week server-side)
  useEffect(() => {
    let cancel = false;
    axios.post(`${API}/rankings/weekly/claim`).then((r) => {
      if (cancel) return;
      const total = r.data?.total_coins || 0;
      if (total > 0) {
        const positions = (r.data.awarded || []).map(a => `#${a.position}`).join(" e ");
        toast.success(`🏆 +${total} moedas por chegar em ${positions} no Top da Semana!`, {
          duration: 8000,
          description: "Continue postando e compartilhando para subir mais no ranking.",
        });
      }
    }).catch(() => { /* silent */ });
    return () => { cancel = true; };
  }, []);

  const [boostPost, setBoostPost] = useState(null);

  const submit = async () => {
    // Merge single-URL (from URL input) + uploaded array
    const gallery = [...imageUrls, ...(imageUrl ? [imageUrl] : [])].filter(Boolean).slice(0, 10);
    if (!content.trim() && gallery.length === 0 && !videoUrl) return;
    try {
      const resp = await axios.post(`${API}/posts`, {
        content: content || (videoUrl ? "🎥 Novo vídeo" : ""),
        image_url: gallery[0] || null,      // legacy
        image_urls: gallery.length ? gallery : null,
        album_title: gallery.length >= 3 && albumTitle.trim() ? albumTitle.trim() : null,
        video_url: videoUrl || null,
        visibility,
        tagged_user_ids: taggedUsers.map((u) => u.user_id),
        mention_ids: mentionPicked.filter((u) => content.toLowerCase().includes(`@${(u.name || "").split(" ")[0].toLowerCase()}`)).map((u) => u.user_id),
        music,
        location,
        link_preview: linkPrev.preview || null,
        skip_link_preview: linkPrev.dismissed,
      });
      linkPrev.reset();
      setMentionPicked([]);
      setContent(""); setImageUrl(""); setImageUrls([]); setAlbumTitle(""); setVideoUrl(""); setShowImage(false); setTaggedUsers([]); setMusic(null); setLocation(null);
      toast.success(visibility === "private" ? "Publicado só para seguidores 🔒" : "Publicado!");
      // Optimistic: show the new post at the top immediately, then sync with the server
      if (resp?.data?.post_id) {
        setPosts((prev) => [{ ...resp.data, reactions: {}, reactions_total: 0 }, ...prev.filter((p) => p.post_id !== resp.data.post_id)]);
      }
      loadPosts();
      missionsRef.current?.refresh?.();
    } catch { toast.error("Erro ao publicar"); }
  };

  const toggleLike = async (id) => {
    const res = await axios.post(`${API}/posts/${id}/like`);
    setPosts(posts.map(p => p.post_id === id ? { ...p, likes: res.data.likes } : p));
    missionsRef.current?.refresh?.();
  };

  const updateComments = (id, fn) => {
    setPosts((prev) => prev.map((p) => p.post_id === id ? { ...p, comments: fn(p.comments || []) } : p));
    missionsRef.current?.refresh?.();
  };

  const notInterested = async (p) => {
    try {
      const r = await axios.post(`${API}/posts/${p.post_id}/not-interested`);
      hidePostLocally(p.post_id);
      setHiddenIds((prev) => new Set([...prev, p.post_id]));
      const who = r.data?.learned?.author_name;
      toast.success(who ? `Vamos mostrar menos posts de ${who}` : "Vamos mostrar menos disso", {
        description: "O feed aprendeu com a sua escolha.",
        action: {
          label: "Desfazer",
          onClick: async () => {
            try {
              await axios.delete(`${API}/posts/${p.post_id}/not-interested`);
              unhidePostLocally(p.post_id);
              setHiddenIds((prev) => { const n = new Set(prev); n.delete(p.post_id); return n; });
            } catch { toast.error("Não foi possível desfazer"); }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar");
    }
  };

  const handleUploaded = (url, kind) => {
    if (kind === "video") { setVideoUrl(url); setImageUrl(""); setImageUrls([]); }
    else {
      // Append to the gallery (max 10). Composer supports both photo & multi-photo posts.
      setImageUrls((prev) => (prev.length >= 10 ? prev : [...prev, url]));
      setVideoUrl("");
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <CompleteLocationBanner />
      {/* Live sessions row */}
      <LivesRow lives={lives} onOpenLive={(id) => setViewerLiveId(id)} />
      <ScheduledLivesRow
        items={scheduledLives}
        onStart={(s) => { setScheduledToStart(s); setBroadcastOpen(true); }}
        onCancel={cancelScheduledLive}
      />

      {/* Stories row (24h) */}
      <StoriesRow />

      {/* Composer */}
      <Card className="p-4" id="feed-composer" data-testid="feed-composer">
        <div className="flex items-start gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={user?.picture} />
            <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-3">
            <div className="relative">
              <Textarea
                ref={contentRef}
                data-testid="post-content"
                placeholder={t("whatsOnMind")}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                className="resize-none border-none focus-visible:ring-0 p-0 text-base bg-transparent"
              />
              <MentionPicker value={content} onChange={setContent} inputRef={contentRef} onPick={(u) => setMentionPicked((p) => (p.some((x) => x.user_id === u.user_id) ? p : [...p, u]))} />
            </div>
            {(linkPrev.preview || linkPrev.loading) && (
              <LinkPreviewCard preview={linkPrev.preview} loading={linkPrev.loading} onRemove={linkPrev.dismiss} testid="composer-link-preview" />
            )}
            {showImage && (
              <Input data-testid="post-image-url" placeholder={t("imageUrl")} value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
            )}
            {imageUrl && !imageUrls.length && (
              <div className="relative">
                <img src={imageUrl} alt="preview" className="rounded-xl w-full max-h-64 object-cover border border-border" />
                <button onClick={() => setImageUrl("")} className="absolute top-2 right-2 bg-black/70 text-white rounded-full w-7 h-7 text-xs" data-testid="post-remove-image">✕</button>
              </div>
            )}
            {imageUrls.length > 0 && (
              <div data-testid="post-gallery-preview" className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{imageUrls.length}/10 fotos</span>
                  {imageUrls.length >= 3 && (
                    <span className="text-primary font-medium" data-testid="album-suggested-badge">
                      📚 Criar álbum
                    </span>
                  )}
                </div>
                {imageUrls.length >= 3 && (
                  <div className="rounded-lg border border-dashed border-primary/50 bg-primary/5 p-3 space-y-2" data-testid="album-composer">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <span>📚</span>
                      <span>Álbum de {imageUrls.length} fotos</span>
                    </div>
                    <Input
                      data-testid="album-title-input"
                      placeholder="Dê um nome ao álbum (ex: Viagem para o Rio)"
                      value={albumTitle}
                      onChange={(e) => setAlbumTitle(e.target.value.slice(0, 80))}
                      maxLength={80}
                      className="h-9 text-sm"
                    />
                    <div className="text-[11px] text-muted-foreground">
                      Toque na ⭐ na foto que será a capa do álbum
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2">
                  {imageUrls.map((u, i) => (
                    <div
                      key={u + "-" + i}
                      className={`relative aspect-square rounded-lg overflow-hidden border bg-muted ${i === 0 ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
                      data-testid={`post-gallery-item-${i}`}
                    >
                      <img src={u} alt={`preview-${i}`} className="w-full h-full object-cover" />
                      {/* Cover star — only when 3+ photos (album mode) */}
                      {imageUrls.length >= 3 && (
                        <button
                          type="button"
                          onClick={() => setImageUrls((prev) => {
                            if (i === 0) return prev;
                            const next = [...prev];
                            const [picked] = next.splice(i, 1);
                            next.unshift(picked);
                            return next;
                          })}
                          className={`absolute bottom-1 left-1 w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all ${
                            i === 0
                              ? "bg-primary text-primary-foreground shadow-md"
                              : "bg-black/60 hover:bg-primary text-white"
                          }`}
                          data-testid={`post-gallery-cover-${i}`}
                          aria-label={i === 0 ? "Capa do álbum" : "Definir como capa"}
                          title={i === 0 ? "Capa do álbum" : "Definir como capa"}
                        >
                          {i === 0 ? "★" : "☆"}
                        </button>
                      )}
                      <button
                        onClick={() => setImageUrls((prev) => prev.filter((_, k) => k !== i))}
                        className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                        data-testid={`post-gallery-remove-${i}`}
                        aria-label="Remover foto"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {videoUrl && (
              <div className="relative">
                <video src={videoUrl} className="rounded-xl w-full max-h-64 bg-black border border-border" controls muted playsInline />
                <button onClick={() => setVideoUrl("")} className="absolute top-2 right-2 bg-black/70 text-white rounded-full w-7 h-7 text-xs" data-testid="post-remove-video">✕</button>
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1 flex-wrap">
                <Button variant="ghost" size="sm" onClick={() => setShowImage(!showImage)} data-testid="post-add-image">
                  <ImageIcon className="w-4 h-4 mr-2 text-green-600" /> URL
                </Button>
                <ImageUploader
                  testid="post-upload-btn"
                  variant="ghost"
                  size="sm"
                  label={imageUrls.length ? `Foto (${imageUrls.length}/10)` : "Foto"}
                  accept="image/*"
                  multiple
                  maxFiles={10 - imageUrls.length}
                  onUploaded={(url) => handleUploaded(url, "photo")}
                />
                <ImageUploader
                  testid="post-upload-video-btn"
                  variant="ghost"
                  size="sm"
                  label="Vídeo"
                  accept="video/*"
                  onUploaded={(url) => handleUploaded(url, "video")}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBroadcastOpen(true)}
                  data-testid="post-go-live-btn"
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                >
                  <Radio className="w-4 h-4 mr-2" /> Ao vivo
                </Button>
                <div className="px-2"><TagPeoplePicker value={taggedUsers} onChange={setTaggedUsers} excludeUserId={user?.user_id} /></div>
                <div className="px-2"><MusicPicker value={music} onChange={setMusic} /></div>
                {music && imageUrls.length > 1 && (
                  <label className="px-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="post-slide-seconds-label">
                    Tempo por foto
                    <select
                      value={music.slide_seconds || 4}
                      onChange={(e) => setMusic({ ...music, slide_seconds: Number(e.target.value) })}
                      data-testid="post-slide-seconds"
                      className="h-7 rounded-full border border-border bg-background px-2 text-xs"
                    >
                      {[2, 3, 4, 5, 7, 10, 15].map((s) => <option key={s} value={s}>{s}s</option>)}
                    </select>
                  </label>
                )}
                <div className="px-2"><LocationPicker value={location} onChange={setLocation} /></div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  data-testid="post-visibility"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                  className="h-9 text-xs px-2 rounded-md border border-border bg-background"
                >
                  <option value="public">🌍 Público</option>
                  <option value="private">🔒 Só seguidores</option>
                </select>
                <Button data-testid="post-submit" onClick={submit} className="rounded-full" size="sm">
                  <Send className="w-4 h-4 mr-2" /> {t("post")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Daily missions */}
      <DailyMissions ref={missionsRef} />

      {/* New posts banner */}
      {newerCount > 0 && (
        <button
          data-testid="new-posts-banner"
          onClick={loadPosts}
          className="w-full py-2 rounded-full bg-primary text-primary-foreground font-semibold text-sm shadow-lg animate-pulse hover:animate-none"
        >
          ▲ {newerCount} nova{newerCount > 1 ? "s" : ""} publicaç{newerCount > 1 ? "ões" : "ão"} — clique para atualizar
        </button>
      )}

      {/* All registered users on the platform */}
      <AllUsersStrip />

      {/* Suggestions for you */}
      {suggestions.length > 0 && (
        <Card className="p-4 bg-gradient-to-br from-purple-500/5 to-primary/5" data-testid="suggestions-section">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-primary" />
            <div className="font-bold text-sm">Sugeridas para você</div>
            <span className="text-[10px] text-muted-foreground">com base em quem você segue e engajamento</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {suggestions.map(s => (
              <Link
                key={s.post_id}
                to={`/user/${s.user_id}`}
                data-testid={`suggested-${s.post_id}`}
                className="min-w-[220px] max-w-[220px] snap-start rounded-xl border bg-background hover:border-primary transition-colors"
              >
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-6 h-6"><AvatarImage src={s.author_picture} /><AvatarFallback className="text-[9px]">{s.author_name?.[0]}</AvatarFallback></Avatar>
                    <span className="text-xs font-semibold truncate">{s.author_name}</span>
                  </div>
                  <p className="text-xs line-clamp-3 opacity-80">{s.content}</p>
                  {s.image_url && <img src={s.image_url} alt="" className="w-full h-20 object-cover rounded mt-2" />}
                  <div className="mt-2 text-[10px] text-muted-foreground">❤ {(s.likes || []).length} · 💬 {(s.comments || []).length}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <PageSuggestions />

      {/* Posts */}
      <div className="space-y-6 stagger">
        {posts.length === 0 && (
          <Card className="p-12 text-center text-muted-foreground">
            <p>Seja o primeiro a publicar!</p>
          </Card>
        )}
        {posts.filter((p) => !hiddenIds.has(p.post_id)).map((p) => (
          <PostCard
            key={p.post_id}
            post={p}
            user={user}
            t={t}
            toggleLike={toggleLike}
            openC={openComments[p.post_id]}
            openComments={openComments}
            setOpenComments={setOpenComments}
            updateComments={updateComments}
            autoplayEnabled={autoplayEnabled}
            videoMuted={videoMuted}
            setVideoMuted={setVideoMuted}
            onReposted={(rp) => setPosts((prev) => [rp, ...prev])}
            onNotInterested={notInterested}
            onOpenFullscreen={(payload) => {
              if (payload?.isImage) setFullscreenImage({ url: payload.url, urls: payload.urls || null, index: payload.index || 0, music: payload.music || null });
              else if (payload?.postId) setReelsStartId(payload.postId);
              else setFullscreenVideo(payload);
            }}
            onEdited={(updated) => setPosts((prev) => prev.map((x) => x.post_id === updated.post_id ? { ...x, ...updated } : x))}
            onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.post_id !== id))}
            onHidden={(id) => setHiddenIds((prev) => new Set([...prev, id]))}
            onBoost={(post) => setBoostPost({ post_id: post.post_id, content: post.content || post.original_content || "", image_url: (post.image_urls && post.image_urls[0]) || post.image_url || post.cover_url || null, video_url: post.video_url || null })}
          />
        ))}
      </div>

      {/* Fullscreen video player */}
      <FullscreenVideoPlayer video={fullscreenVideo} onClose={() => setFullscreenVideo(null)} />
      {/* TikTok/Reels-style vertical video feed */}
      {reelsStartId && (
        <VideoReels
          videos={posts.filter((p) => !!p.video_url)}
          startId={reelsStartId}
          onClose={() => setReelsStartId(null)}
          onLike={toggleLike}
          onComment={async (postId, text) => {
            const res = await axios.post(`${API}/posts/${postId}/comment`, { content: text });
            setPosts((prev) => prev.map((p) => p.post_id === postId ? { ...p, comments: [...(p.comments || []), res.data] } : p));
          }}
          initialMuted={videoMuted}
        />
      )}
      {/* Fullscreen image viewer (posts + banners) */}
      <FullscreenImageViewer src={fullscreenImage?.url} images={fullscreenImage?.urls} startIndex={fullscreenImage?.index || 0} music={fullscreenImage?.music} onClose={() => setFullscreenImage(null)} />

      {/* Broadcast dialog (owner) */}
      <LiveBroadcastDialog
        open={broadcastOpen}
        onOpenChange={(v) => { setBroadcastOpen(v); if (!v) setScheduledToStart(null); }}
        scheduled={scheduledToStart}
        onScheduled={() => { loadLives(); }}
        onStarted={() => { loadLives(); }}
        onEnded={() => { loadLives(); }}
        onRecordingPublished={(post) => { if (post?.post_id) setPosts((prev) => [{ ...post, reactions: {}, reactions_total: 0 }, ...prev.filter((p) => p.post_id !== post.post_id)]); }}
      />

      {/* Viewer dialog */}
      <LiveViewerDialog
        liveId={viewerLiveId}
        onClose={() => { setViewerLiveId(null); loadLives(); }}
      />

      {/* Boost prompt after publishing a post with media */}
      <BoostPostDialog open={!!boostPost} onOpenChange={(v) => !v && setBoostPost(null)} post={boostPost} />
    </div>
  );
}
